import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import Table from './Table';
import CardView from './CardView';
import ViewToggle from './ViewToggle';

// Renders children at document.body, anchored to a trigger element's bounding rect.
// Recalculates position on scroll/resize so the popover follows the trigger.
const Popover = ({ open, anchorRef, onClose, width = 320, children, align = 'right' }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  const recompute = useCallback(() => {
    if (!anchorRef?.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const top = r.bottom + 8;
    let left;
    if (align === 'right') {
      left = r.right - width;
    } else {
      left = r.left;
    }
    // Keep within viewport
    const maxLeft = window.innerWidth - width - 8;
    if (left < 8) left = 8;
    if (left > maxLeft) left = maxLeft;
    setPos({ top, left });
  }, [anchorRef, width, align]);

  useEffect(() => {
    if (!open) return;
    recompute();
    const handle = () => recompute();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [open, recompute]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose && onClose();
    };
    const handleEsc = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width, zIndex: 1000 }}
      className="bg-white border border-gray-200 rounded-lg shadow-xl"
    >
      {children}
    </div>,
    document.body
  );
};

const DataDisplay = ({
  columns = [],
  data = [],
  onSort,
  onCheckAll,
  onCheckRow,
  selectedRows = [],
  sortConfig,
  onUpdateCell,
  onRowClick,
  onColumnReorder,
  title,
  pagination,
  onPageChange,
  currentPage,
  // Pagination options
  itemsPerPage = 10,
  onItemsPerPageChange,
  showViewAll = true,
  // Project Manager Filter Props
  showProjectManagerFilter = false,
  selectedProjectManager,
  showProjectManagerDropdown,
  setShowProjectManagerDropdown,
  pmSearchTerm,
  setPmSearchTerm,
  handleSetProjectManager,
  handleClearProjectManager,
  projectManagerDropdownRef,
  getAllProjectManagers,
  getFilteredProjectManagers,
  getInitials,
  // Date Filter Props
  showDateFilter = false,
  dateRange,
  setDateRange,
  showDatePicker,
  setShowDatePicker,
  datePickerDropdownRef,
  getDateRangeDisplay,
  clearDateRange
}) => {
  const { viewMode } = useSelector(state => state.view);

  // Internal refs for trigger buttons used by portaled popovers
  const dateBtnRef = useRef(null);
  const pmBtnRef = useRef(null);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        {title && (
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        )}
        <div className="flex-1"></div>
        {/* Desktop Filters */}
        <div className="hidden xl:flex items-center gap-3 mr-4">
          {/* Date Range Filter - Desktop */}
          {showDateFilter && (
            <div ref={datePickerDropdownRef}>
              <button
                ref={dateBtnRef}
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 ${
                  (dateRange?.startDate || dateRange?.endDate)
                    ? 'border-green-200 bg-green-50 text-green-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:shadow-sm'
                }`}
                title="Filter by Date Range"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">
                  {getDateRangeDisplay ? getDateRangeDisplay() : 'All Time'}
                </span>
                {(dateRange?.startDate || dateRange?.endDate) && (
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                )}
              </button>

              <Popover
                open={!!showDatePicker}
                anchorRef={dateBtnRef}
                width={320}
                onClose={() => setShowDatePicker(false)}
              >
                <div onMouseDown={(e) => e.stopPropagation()}>
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="text-sm font-medium text-gray-900">Filter by Date Range</h3>
                    <p className="text-xs text-gray-500 mt-1">Select date range to filter data</p>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Start Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={dateRange?.startDate ? dateRange.startDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value) : null;
                          setDateRange && setDateRange(prev => ({ ...prev, startDate: date }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>

                    {/* End Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={dateRange?.endDate ? dateRange.endDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value) : null;
                          setDateRange && setDateRange(prev => ({ ...prev, endDate: date }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>

                    {/* Quick Date Options */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quick Options</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            const today = new Date();
                            const thirtyDaysAgo = new Date(today);
                            thirtyDaysAgo.setDate(today.getDate() - 30);
                            setDateRange && setDateRange({ startDate: thirtyDaysAgo, endDate: today });
                          }}
                          className="px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          Last 30 days
                        </button>
                        <button
                          onClick={() => {
                            const today = new Date();
                            const ninetyDaysAgo = new Date(today);
                            ninetyDaysAgo.setDate(today.getDate() - 90);
                            setDateRange && setDateRange({ startDate: ninetyDaysAgo, endDate: today });
                          }}
                          className="px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          Last 90 days
                        </button>
                        <button
                          onClick={() => {
                            const today = new Date();
                            const startOfYear = new Date(today.getFullYear(), 0, 1);
                            setDateRange && setDateRange({ startDate: startOfYear, endDate: today });
                          }}
                          className="px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          This year
                        </button>
                        <button
                          onClick={() => clearDateRange && clearDateRange()}
                          className="px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Popover>
            </div>
          )}

          {/* Project Manager Filter */}
          {showProjectManagerFilter && (
            <div ref={projectManagerDropdownRef}>
              <button
                ref={pmBtnRef}
                onClick={() => setShowProjectManagerDropdown(!showProjectManagerDropdown)}
                className={`group flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 ${
                  selectedProjectManager
                    ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:shadow-sm'
                }`}
                title="Filter by Project Manager"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="text-sm font-medium">
                  {selectedProjectManager
                    ? `${selectedProjectManager.profile?.firstName} ${selectedProjectManager.profile?.lastName}`
                    : 'Filter by PM'
                  }
                </span>
                {selectedProjectManager && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                )}
              </button>

              <Popover
                open={!!showProjectManagerDropdown}
                anchorRef={pmBtnRef}
                width={320}
                onClose={() => { setShowProjectManagerDropdown(false); setPmSearchTerm(''); }}
              >
                <div onMouseDown={(e) => e.stopPropagation()}>
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="text-sm font-medium text-gray-900">Filter by Project Manager</h3>
                    <p className="text-xs text-gray-500 mt-1">Select a project manager to filter leads</p>

                    {/* Search Input */}
                    <div className="mt-3">
                      <div className="relative">
                        <input
                          type="text"
                          value={pmSearchTerm}
                          onChange={(e) => setPmSearchTerm(e.target.value)}
                          placeholder="Search project managers..."
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        {pmSearchTerm && (
                          <button
                            onClick={() => setPmSearchTerm('')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="max-h-64 overflow-auto">
                    <button
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50"
                      onClick={handleClearProjectManager}
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">All Project Managers</div>
                        <div className="text-xs text-gray-500">Show leads for all project managers</div>
                      </div>
                      {!selectedProjectManager && (
                        <div className="ml-auto">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                      )}
                    </button>

                    {getFilteredProjectManagers && getFilteredProjectManagers().map(manager => (
                      <button
                        key={manager._id}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3"
                        onClick={() => handleSetProjectManager(manager)}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm">
                          {getInitials && getInitials(manager.profile?.firstName, manager.profile?.lastName)}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {manager.profile?.firstName} {manager.profile?.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {manager.profile?.role?.replace('_', ' ') || 'N/A'}
                          </div>
                        </div>
                        {selectedProjectManager?._id === manager._id && (
                          <div className="ml-auto">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          </div>
                        )}
                      </button>
                    ))}

                    {pmSearchTerm && getFilteredProjectManagers && getFilteredProjectManagers().length === 0 && (
                      <div className="px-4 py-6 text-center text-sm text-gray-500">
                        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <div>No results found for "{pmSearchTerm}"</div>
                        <button
                          onClick={() => setPmSearchTerm('')}
                          className="text-blue-600 hover:text-blue-700 text-xs mt-1"
                        >
                          Clear search
                        </button>
                      </div>
                    )}

                    {!pmSearchTerm && getAllProjectManagers && getAllProjectManagers().length === 0 && (
                      <div className="px-4 py-6 text-center text-sm text-gray-500">
                        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        No project managers found
                      </div>
                    )}
                  </div>
                </div>
              </Popover>
            </div>
          )}
        </div>
        
        {/* ViewToggle - Always visible on all screen sizes */}
        <ViewToggle />
      </div>

      {/* Mobile/Tablet Filters - Above Table/Cards */}
      <div className="xl:hidden mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Mobile Date Picker Button */}
          {showDateFilter && (
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 ${
                (dateRange?.startDate || dateRange?.endDate)
                  ? 'border-green-200 bg-green-50 text-green-700 shadow-sm' 
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:shadow-sm'
              }`}
              title="Filter by Date Range"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium">
                {(dateRange?.startDate || dateRange?.endDate) 
                  ? (getDateRangeDisplay ? getDateRangeDisplay() : 'Date Range') 
                  : 'Date Range'
                }
              </span>
              {(dateRange?.startDate || dateRange?.endDate) && (
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              )}
            </button>
          )}

          {/* Mobile Project Manager Button */}
          {showProjectManagerFilter && (
            <button
              onClick={() => setShowProjectManagerDropdown(!showProjectManagerDropdown)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 ${
                selectedProjectManager 
                  ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm' 
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:shadow-sm'
              }`}
              title="Filter by Project Manager"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="text-sm font-medium">
                {selectedProjectManager 
                  ? `${selectedProjectManager.profile?.firstName} ${selectedProjectManager.profile?.lastName}` 
                  : 'Project Manager'
                }
              </span>
              {selectedProjectManager && (
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              )}
            </button>
          )}
        </div>
      </div>
      
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className={`flex-1 ${viewMode === 'card' ? 'overflow-y-auto' : 'overflow-x-auto'}`}>
          {viewMode === 'table' ? (
            <Table
              columns={columns}
              data={data}
              onSort={onSort}
              onCheckAll={onCheckAll}
              onCheckRow={onCheckRow}
              selectedRows={selectedRows}
              sortConfig={sortConfig}
              onUpdateCell={onUpdateCell}
              onRowClick={onRowClick}
              onColumnReorder={onColumnReorder}
            />
          ) : (
            <CardView
              columns={columns}
              data={data}
              onRowClick={onRowClick}
              onCheckRow={onCheckRow}
              selectedRows={selectedRows}
            />
          )}
        </div>

        {pagination && (
          <div className="mt-4 pt-4 border-t border-gray-200 bg-white">
            {/* Mobile-first responsive pagination */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Items per page selector - Full width on mobile */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">Items per page:</label>
                  <select
                    value={itemsPerPage === 'all' ? 'all' : itemsPerPage}
                    onChange={(e) => {
                      const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
                      onItemsPerPageChange && onItemsPerPageChange(value);
                    }}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[40px]"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    {showViewAll && <option value="all">All</option>}
                  </select>
                </div>
                <div className="text-sm text-gray-600">
                  {itemsPerPage === 'all' 
                    ? `Showing all ${pagination.total} items`
                    : `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, pagination.total)}-${Math.min(currentPage * itemsPerPage, pagination.total)} of ${pagination.total} items`
                  }
                </div>
              </div>

              {/* Page navigation - Mobile optimized */}
              {itemsPerPage !== 'all' && pagination.pages > 1 && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="text-sm text-gray-600 text-center sm:text-left">
                    Page {currentPage} of {pagination.pages}
                  </div>
                  <div className="flex justify-center sm:justify-start space-x-2">
                    <button
                      onClick={() => onPageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className={`px-4 py-2 min-h-[44px] text-sm font-medium rounded-lg border transition-colors
                        ${currentPage <= 1 
                          ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 active:bg-gray-100'
                        }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => onPageChange(currentPage + 1)}
                      disabled={currentPage >= pagination.pages}
                      className={`px-4 py-2 min-h-[44px] text-sm font-medium rounded-lg border transition-colors
                        ${currentPage >= pagination.pages
                          ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 active:bg-gray-100'
                        }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Date Range Filter Dialog for Mobile/Tablet */}
      {showDatePicker && (
        <>
          <style>{`
            @keyframes slide-up {
              from {
                transform: translateY(100%);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            .animate-slide-up {
              animation: slide-up 0.3s ease-out;
            }
          `}</style>
          <div 
            className="xl:hidden fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 pb-safe"
            onClick={() => setShowDatePicker(false)}
          >
            <div 
              className="bg-white rounded-t-xl w-full max-w-md mx-4 animate-slide-up max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Filter by Date Range</h3>
                  <button
                    onClick={() => setShowDatePicker(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Select date range to filter data</p>
              </div>
              
              <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={dateRange?.startDate ? dateRange.startDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value ? new Date(e.target.value) : null;
                      setDateRange && setDateRange(prev => ({ ...prev, startDate: date }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                
                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={dateRange?.endDate ? dateRange.endDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value ? new Date(e.target.value) : null;
                      setDateRange && setDateRange(prev => ({ ...prev, endDate: date }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                
                {/* Quick Date Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quick Options</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        const today = new Date();
                        const thirtyDaysAgo = new Date(today);
                        thirtyDaysAgo.setDate(today.getDate() - 30);
                        setDateRange && setDateRange({ startDate: thirtyDaysAgo, endDate: today });
                      }}
                      className="px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Last 30 days
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date();
                        const ninetyDaysAgo = new Date(today);
                        ninetyDaysAgo.setDate(today.getDate() - 90);
                        setDateRange && setDateRange({ startDate: ninetyDaysAgo, endDate: today });
                      }}
                      className="px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Last 90 days
                    </button>
                    <button
                      onClick={() => {
                        const today = new Date();
                        const startOfYear = new Date(today.getFullYear(), 0, 1);
                        setDateRange && setDateRange({ startDate: startOfYear, endDate: today });
                      }}
                      className="px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      This year
                    </button>
                    <button
                      onClick={() => {
                        clearDateRange && clearDateRange();
                        setShowDatePicker(false);
                      }}
                      className="px-3 py-2 text-sm bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Project Manager Filter Dialog for Mobile/Tablet */}
      {showProjectManagerDropdown && (
        <>
          <style>{`
            @keyframes slide-up {
              from {
                transform: translateY(100%);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            .animate-slide-up {
              animation: slide-up 0.3s ease-out;
            }
          `}</style>
          <div 
            className="xl:hidden fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 pb-safe"
            onClick={() => setShowProjectManagerDropdown(false)}
          >
            <div 
              className="bg-white rounded-t-xl w-full max-w-md mx-4 animate-slide-up max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Filter by Project Manager</h3>
                  <button
                    onClick={() => setShowProjectManagerDropdown(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Select a project manager to filter leads</p>
                
                {/* Search Input */}
                <div className="mt-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={pmSearchTerm}
                      onChange={(e) => setPmSearchTerm(e.target.value)}
                      placeholder="Search project managers..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    {pmSearchTerm && (
                      <button
                        onClick={() => setPmSearchTerm('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="max-h-64 overflow-auto">
                <button
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50"
                  onClick={handleClearProjectManager}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">All Project Managers</div>
                    <div className="text-xs text-gray-500">Show leads for all project managers</div>
                  </div>
                  {!selectedProjectManager && (
                    <div className="ml-auto">
                      <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  )}
                </button>
                
                {getFilteredProjectManagers && getFilteredProjectManagers().map(manager => (
                  <button
                    key={manager._id}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3"
                    onClick={() => handleSetProjectManager(manager)}
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm">
                      {getInitials && getInitials(manager.profile?.firstName, manager.profile?.lastName)}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {manager.profile?.firstName} {manager.profile?.lastName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {manager.profile?.role?.replace('_', ' ') || 'N/A'}
                      </div>
                    </div>
                    {selectedProjectManager?._id === manager._id && (
                      <div className="ml-auto">
                        <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
                
                {pmSearchTerm && getFilteredProjectManagers && getFilteredProjectManagers().length === 0 && (
                  <div className="px-4 py-6 text-center text-sm text-gray-500">
                    <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <div>No results found for "{pmSearchTerm}"</div>
                    <button
                      onClick={() => setPmSearchTerm('')}
                      className="text-blue-600 hover:text-blue-700 text-xs mt-1"
                    >
                      Clear search
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DataDisplay; 